import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getLinkPreview } from "../api/announcements";
import MintlyLinkCard, { mintlyLinkTarget, mintlyPath } from "./MintlyLinkCard";
import Icon from "./ds/Icon";
import { STATE_LAYER } from "./ds/IconButton";

const LINK = /https?:\/\/[^\s<>"']+/gi;
// A link at the end of a sentence keeps its URL, not the full stop after it.
const trimEnd = (url) => url.replace(/[).,!?:;'"]+$/, "");

/** The links in a post's text, in order. */
export const findLinks = (text) => (String(text || "").match(LINK) || []).map(trimEnd);

/** A post's text with its links clickable. */
export const LinkifiedText = ({ text, className = "" }) => {
  const source = String(text || "");
  const parts = [];
  let last = 0;
  for (const match of source.matchAll(LINK)) {
    const url = trimEnd(match[0]);
    const inApp = mintlyPath(url);
    parts.push(source.slice(last, match.index));
    // Mintly's own addresses open inside the app; other sites in a new tab.
    parts.push(
      inApp ? (
        <Link
          key={match.index}
          to={inApp}
          onClick={(event) => event.stopPropagation()}
          className="break-all text-accent-text hover:underline"
        >
          {url}
        </Link>
      ) : (
      <a
        key={match.index}
        href={url}
        target="_blank"
        rel="noopener noreferrer nofollow"
        onClick={(event) => event.stopPropagation()}
        className="break-all text-accent-text hover:underline"
      >
        {url}
      </a>
      )
    );
    last = match.index + url.length;
  }
  parts.push(source.slice(last));
  return <p className={className}>{parts}</p>;
};

// One request per address for the whole session, however many posts share it.
const previews = new Map();
const loadPreview = (url) => {
  if (!previews.has(url)) {
    previews.set(
      url,
      getLinkPreview(url)
        .then((res) => (res?.success ? res.data : null))
        .catch(() => null)
    );
  }
  return previews.get(url);
};

/*
  The card under a post that links another site: its picture, site, title and description, read
  by the server. Until that arrives, or if the page offers nothing, the card shows the
  address itself, so a link is never left without one.
*/
const WebLinkCard = ({ url }) => {
  const [data, setData] = useState(null);
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setImageFailed(false);
    loadPreview(url).then((value) => alive && setData(value));
    return () => {
      alive = false;
    };
  }, [url]);

  let host = url;
  try {
    host = new URL(url).hostname.replace(/^www\./, "");
  } catch {
    // An address the browser can't read still shows as typed.
  }
  const image = data?.image && !imageFailed ? data.image : "";

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={(event) => event.stopPropagation()}
      className={`relative flex flex-col overflow-hidden rounded-radius-md border border-separator-default bg-fill-quaternary ${STATE_LAYER}`}
    >
      {image && (
        <img
          src={image}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setImageFailed(true)}
          className="max-h-[260px] w-full object-cover"
        />
      )}
      <span className="flex items-center gap-space-12 p-space-12">
        {!image && (
          <span className="grid size-10 shrink-0 place-items-center rounded-radius-sm bg-fill-secondary text-label-secondary">
            <Icon name="link" size={20} />
          </span>
        )}
        <span className="flex min-w-0 flex-col gap-[2px]">
          <span className="truncate text-caption-1 text-label-tertiary">{data?.siteName || host}</span>
          <span className="line-clamp-2 break-words text-subheadline-semibold text-label-primary">{data?.title || host}</span>
          {data?.description && (
            <span className="line-clamp-2 break-words text-footnote text-label-secondary">{data.description}</span>
          )}
        </span>
      </span>
    </a>
  );
};

/** A Mintly product, space, campaign or profile gets its own card; any other page the plain one. */
const LinkPreviewCard = ({ url }) =>
  mintlyLinkTarget(url) ? <MintlyLinkCard href={url} fallback={<WebLinkCard url={url} />} /> : <WebLinkCard url={url} />;

export default LinkPreviewCard;
